import cn from 'classnames';
import { CSSProperties, FC } from 'react';

import { DentalNotationFormat } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { SUPERNUMERARY_INDEX_BY_USER } from '@/shared/config';
import { Icon } from '@/shared/ui';

import { LocalizationWithISO, getDisplayToothNumber } from '@/entities/tooth';

import styles from './ImageWithTeethNumbers.module.scss';

type ImageWithTeethNumbersProps = {
  className?: string;
  testID?: string;
  onToothClick: (
    viewedISONumbers: number[],
    localization: LocalizationWithISO,
  ) => void;
  localizations: LocalizationWithISO[];
  src: string;
  dentalNotation: DentalNotationFormat;
  scale: number;
  style?: CSSProperties;
};

export const ImageWithTeethNumbers: FC<ImageWithTeethNumbersProps> = (
  props,
) => {
  const {
    className,
    testID,
    onToothClick,
    localizations = [],
    src,
    dentalNotation = DentalNotationFormat.DentalNotationFormatISO,
    scale,
    style,
  } = props;

  const { duplicatedISONumbers, viewedISONumbers } = localizations.reduce(
    (accum, localization) => {
      if (localization.isRemoved) {
        return accum;
      }

      if (
        localization.ISONumber !== 404 &&
        localizations.some(
          (item) =>
            !item.isRemoved &&
            item.ISONumber === localization.ISONumber &&
            item.ID !== localization.ID &&
            (item.SupernumeraryIndex === localization.SupernumeraryIndex ||
              item.SupernumeraryIndex === SUPERNUMERARY_INDEX_BY_USER),
        )
      ) {
        accum.duplicatedISONumbers.push(localization.ISONumber);
      } else {
        accum.viewedISONumbers.push(localization.ISONumber);
      }

      return accum;
    },
    { duplicatedISONumbers: [], viewedISONumbers: [] } as {
      duplicatedISONumbers: number[];
      viewedISONumbers: number[];
    },
  );

  return (
    <div
      className={cn(styles.container, className)}
      data-testid={testID}
      data-component="image-with-teeth-numbers"
      style={style}
    >
      <img className={styles.image} src={src} />
      {localizations.map((localization) => {
        const Xmin = localization.BBox?.X?.Min as number | 0;
        const Xmax = localization.BBox?.X?.Max as number | 0;
        const Ymin = localization.BBox?.Y?.Min as number | 0;
        const Ymax = localization.BBox?.Y?.Max as number | 0;

        const centroidX = Xmin + (Xmax - Xmin) / 2;
        const centroidY = Ymin + (Ymax - Ymin) / 2;

        const isDuplicated =
          !localization.isRemoved &&
          duplicatedISONumbers.includes(localization.ISONumber);

        return (
          <div
            onClick={() => {
              onToothClick(viewedISONumbers, localization);
            }}
            key={localization.ID}
            className={cn(
              styles.centroid,
              isDuplicated ? styles.duplicated : '',
            )}
            style={{
              left: centroidX * scale,
              top: centroidY * scale,
            }}
          >
            {localization.ISONumber === 404 || localization.isRemoved ? (
              <Icon name="close" size={20} />
            ) : (
              getDisplayToothNumber(localization.ISONumber, dentalNotation)
            )}
          </div>
        );
      })}
    </div>
  );
};
